import React from 'react';
import type { Annotation } from './types';

interface AnnotationRendererProps {
  annotation: Annotation;
  isSelected?: boolean;
}

function hexToRgba(hex: string, alpha: number): string
{
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const AnnotationRenderer: React.FC<AnnotationRendererProps> = ({ annotation }) =>
{
  const a = annotation;

  // PATH (pencil, highlighter)
  if (a.kind === 'path')
  {
    if (a.points.length < 2) return null;

    const isHighlighter = a.tool === 'highlighter';
    const strokeColor = isHighlighter ? hexToRgba(a.color, 0.45) : a.color;
    const strokeWidth = isHighlighter ? a.lineWidth * 4 : a.lineWidth;

    // Build SVG path with smooth curves
    const first = a.points[0];
    if (!first) return null;

    let pathD = `M ${first.x} ${first.y}`;
    for (let i = 1; i < a.points.length - 1; i++)
    {
      const cur = a.points[i];
      const next = a.points[i + 1];
      if (!cur || !next) continue;
      const mx = (cur.x + next.x) / 2;
      const my = (cur.y + next.y) / 2;
      pathD += ` Q ${cur.x} ${cur.y} ${mx} ${my}`;
    }
    const last = a.points[a.points.length - 1];
    if (last) pathD += ` L ${last.x} ${last.y}`;

    return (
      <path
        d={pathD}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={a.opacity}
      />
    );
  }

  // SHAPE (rect, circle, censor)
  if (a.kind === 'shape')
  {
    const { x, y, width, height } = a;
    const absW = Math.abs(width);
    const absH = Math.abs(height);
    const rectX = width < 0 ? x + width : x;
    const rectY = height < 0 ? y + height : y;

    if (a.tool === 'censor')
    {
      return (
        <rect
          x={rectX}
          y={rectY}
          width={absW}
          height={absH}
          fill={a.color}
          opacity={a.opacity}
        />
      );
    }

    if (a.tool === 'circle')
    {
      const cx = x + width / 2;
      const cy = y + height / 2;
      const rx = Math.abs(width / 2);
      const ry = Math.abs(height / 2);

      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          stroke={a.color}
          strokeWidth={a.lineWidth}
          fill={a.fill ? hexToRgba(a.color, 0.3) : 'none'}
          opacity={a.opacity}
        />
      );
    }

    // rect
    return (
      <rect
        x={rectX}
        y={rectY}
        width={absW}
        height={absH}
        stroke={a.color}
        strokeWidth={a.lineWidth}
        fill={a.fill ? hexToRgba(a.color, 0.3) : 'none'}
        opacity={a.opacity}
      />
    );
  }

  // LINE or ARROW
  if (a.kind === 'line')
  {
    const { x1, y1, x2, y2 } = a;
    const arrowSize = Math.max(10, a.lineWidth * 4);

    if (a.tool === 'arrow')
    {
      // Arrow with head
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headAngle = Math.PI / 6;

      const arrowPoints = [
        `${x2},${y2}`,
        `${x2 - arrowSize * Math.cos(angle - headAngle)},${y2 - arrowSize * Math.sin(angle - headAngle)}`,
        `${x2 - arrowSize * Math.cos(angle + headAngle)},${y2 - arrowSize * Math.sin(angle + headAngle)}`,
      ].join(' ');

      return (
        <g opacity={a.opacity}>
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={a.color}
            strokeWidth={a.lineWidth}
            strokeLinecap="round"
          />
          <polygon
            points={arrowPoints}
            fill={a.color}
          />
        </g>
      );
    }

    // Simple line
    return (
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={a.color}
        strokeWidth={a.lineWidth}
        strokeLinecap="round"
        opacity={a.opacity}
      />
    );
  }

  // BLUR
  if (a.kind === 'blur')
  {
    const { x, y, width, height } = a;
    const absW = Math.abs(width);
    const absH = Math.abs(height);
    const rectX = width < 0 ? x + width : x;
    const rectY = height < 0 ? y + height : y;

    if (absW < 2 || absH < 2) return null;

    // Frosted glass effect with visible texture - more white
    return (
      <g>
        {/* Pixelated background with blur */}
        <rect
          x={rectX}
          y={rectY}
          width={absW}
          height={absH}
          fill="white"
          opacity={0.92}
          filter="url(#blur-glass)"
        />
        {/* Glass texture overlay */}
        <rect
          x={rectX}
          y={rectY}
          width={absW}
          height={absH}
          fill="url(#blur-pattern)"
          opacity={0.85}
        />
      </g>
    );
  }

  // TEXT
  if (a.kind === 'text')
  {
    const { x, y, text, fontSize, bold, italic, color, hasBackground, backgroundColor } = a;
    const fontWeight = bold ? 'bold' : 'normal';
    const fontStyle = italic ? 'italic' : 'normal';

    // Background for readability
    const textLen = text.length * fontSize * 0.6; // Rough estimate
    const boxH = fontSize * 1.3;

    return (
      <g opacity={a.opacity}>
        {/* Background box */}
        {hasBackground && (
          <rect
            x={x - 2}
            y={y - 2}
            width={textLen + 4}
            height={boxH + 4}
            fill={backgroundColor}
          />
        )}
        {/* Text */}
        <text
          x={x}
          y={y}
          fontSize={fontSize}
          fontWeight={fontWeight}
          fontStyle={fontStyle}
          fill={color}
          dominantBaseline="text-before-edge"
          fontFamily="sans-serif"
        >
          {text}
        </text>
      </g>
    );
  }

  // PIN
  if (a.kind === 'pin')
  {
    const { x, y, label, color } = a;
    const r = 14;

    return (
      <g opacity={a.opacity}>
        {/* Drop shadow */}
        <circle
          cx={x}
          cy={y + 2}
          r={r}
          fill="rgba(0,0,0,0.3)"
        />
        {/* Pin circle */}
        <circle
          cx={x}
          cy={y}
          r={r}
          fill={color}
        />
        {/* Pin label */}
        <text
          x={x}
          y={y}
          fontSize={11}
          fontWeight="bold"
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="sans-serif"
        >
          {label}
        </text>
      </g>
    );
  }

  return null;
};
